import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { LineChart } from 'components/Analytics/LineChart';
import { ViewType, StatementDownload } from '../shared/types';
import { Section, Container, Title } from '../shared/styled';
import { BalanceButtons } from '../components/BalanceButtons';
import { BalanceCard } from './BalanceCard';
import { StatementTable } from './StatementTable';
import moment from 'moment';
import {FINANCE_STYLE} from "components/DateRangeDropdown/styles";
import DateRangeDropdown from "components/DateRangeDropdown";
import {DateRangeTrigger, FINANCE_TRIGGER_STYLE} from "components/DateRangeDropdown/components/DateRangeTrigger";
import {findMatchingPreset} from "components/DateRangeDropdown/utils/datePresets";

const PLACEHOLDER_TEXT = "Select a period";

const MOCK_STATEMENTS: StatementDownload[] = [
    { id: '1', month: 'July', year: '2025', amount: 123.45, date: '2025-07-01' },
    { id: '2', month: 'June', year: '2025', amount: 98.30, date: '2025-06-01' },
    { id: '3', month: 'May', year: '2025', amount: 15.78, date: '2025-05-01' },
    { id: '4', month: 'April', year: '2025', amount: 3264.75, date: '2025-04-01' },
    { id: '5', month: 'March', year: '2025', amount: 157.00, date: '2025-03-01' },
    { id: '6', month: 'February', year: '2025', amount: 543.10, date: '2025-02-01' },
];

interface FinancialOverviewProps {
    handleViewChange: (view: ViewType) => void;
    filterDate: Date[];
    onDateChange: (dates: Date[]) => void;
}

export const FinancialOverview: React.FC<FinancialOverviewProps> = ({
                                                                        handleViewChange,
                                                                        filterDate,
                                                                        onDateChange
                                                                    }) => {
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [selectedDatePreset, setSelectedDatePreset] = useState<string>(PLACEHOLDER_TEXT);
    const [activeTab, setActiveTab] = useState<'accounting' | 'insights'>('accounting');
    const [viewChartBy, setViewChartBy] = useState('Totals');
    const [downloadPeriod, setDownloadPeriod] = useState('Last 6 months');

    const periodRangeRef = useRef<HTMLDivElement>(null);

    const [financialData] = useState({
        timeseries: [
            { date: '10.07', plays: '5000', platform_id: '1' },
            { date: '11.07', plays: '8000', platform_id: '1' },
            { date: '12.07', plays: '12070', platform_id: '1' },
            { date: '13.07', plays: '19013', platform_id: '1' },
            { date: '14.07', plays: '15000', platform_id: '1' },
            { date: '15.07', plays: '10000', platform_id: '1' },
            { date: '16.07', plays: '9000', platform_id: '1' },
        ],
    });

    const [financialInfo] = useState({
        balance: 30345.90,
        growthPct: 11.28,
    });

    useEffect(() => {
        if (filterDate && filterDate.length === 2) {
            const matchingPreset = findMatchingPreset(filterDate[0], filterDate[1]);

            if (matchingPreset) {
                setSelectedDatePreset(matchingPreset);
            } else {
                const from = moment(filterDate[0]).format('YYYY-MM-DD');
                const to = moment(filterDate[1]).format('YYYY-MM-DD');
                setSelectedDatePreset(`${from} - ${to}`);
            }
        } else if (!filterDate || filterDate.length === 0) {
            setSelectedDatePreset(PLACEHOLDER_TEXT);
        }
    }, [filterDate]);

    const handleDateRangeChange = (dates: Date[]) => {
        onDateChange(dates);
    };

    const handleClearDateRange = () => {
        onDateChange([]);
        setSelectedDatePreset(PLACEHOLDER_TEXT);
    };

    return (
        <OverviewContainer>
            <Section>
                <Header>
                    <HeaderLeft>
                        <Title>
                            <FormattedMessage
                                id="Finance.Overview.Text"
                                defaultMessage="Financial Overview"
                            />
                        </Title>
                        <DateFilter>
                            <DateRangeTrigger
                                selectedData={selectedDatePreset}
                                isOpen={isDatePickerOpen}
                                onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
                                onClear={handleClearDateRange}
                                triggerRef={periodRangeRef}
                                styleConfig={FINANCE_TRIGGER_STYLE}
                                placeholderText={PLACEHOLDER_TEXT}
                            />
                            <DateRangeDropdown
                                open={isDatePickerOpen}
                                setOpen={setIsDatePickerOpen}
                                setNewData={handleDateRangeChange}
                                selectedData={selectedDatePreset}
                                setSelectedData={setSelectedDatePreset}
                                periodRangeRef={periodRangeRef}
                                styleConfig={FINANCE_STYLE}
                            />
                        </DateFilter>
                    </HeaderLeft>
                    <Actions>
                        <BalanceButtons
                            onTopUp={() => handleViewChange('topup')}
                            onWithdraw={() => handleViewChange('withdraw')}
                        />
                    </Actions>
                </Header>

                <BalanceCard
                    financialInfo={financialInfo}
                    viewChartBy={viewChartBy}
                    onViewChartByChange={setViewChartBy}
                />

                <LineChart timeseries={financialData.timeseries as any} />
            </Section>

            <Section>
                <Tabs>
                    <Tab
                        $active={activeTab === 'accounting'}
                        onClick={() => setActiveTab('accounting')}
                    >
                        <FormattedMessage
                            id="Finance.Tab.Accounting"
                            defaultMessage="Accounting"
                        />
                    </Tab>
                    <Tab
                        $active={activeTab === 'insights'}
                        onClick={() => setActiveTab('insights')}
                    >
                        <FormattedMessage
                            id="Finance.Tab.Insights"
                            defaultMessage="Insights"
                        />
                    </Tab>
                </Tabs>

                {activeTab === 'accounting' && (
                    <StatementTable
                        statements={MOCK_STATEMENTS}
                        period={downloadPeriod}
                        onPeriodChange={setDownloadPeriod}
                    />
                )}

                {activeTab === 'insights' && (
                    <InsightsPlaceholder>
                        <FormattedMessage
                            id="Finance.Insights.ComingSoon"
                            defaultMessage="Financial insights coming soon..."
                        />
                    </InsightsPlaceholder>
                )}
            </Section>
        </OverviewContainer>
    );
};

const OverviewContainer = styled(Container)``;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;

    @media (max-width: 768px) {
        flex-direction: column;
        gap: 16px;
        align-items: flex-start;
    }

    @media (max-width: 1024px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 1rem;
    }
`;

const HeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    
    @media (max-width: 768px) {
        align-items: center;
        justify-content: space-between;
        flex-direction: column;
        gap: 1.5rem;
        width: 100%;
    }
`;

const DateFilter = styled.div`
    position: relative;
    display: flex;
    align-items: center;

    @media (max-width: 768px) {
        width: 100%;
    }
`;

const Actions = styled.aside`
    @media (max-width: 1024px) {
        width: 100%;
    }
`;

const Tabs = styled.div`
    display: flex;
    gap: 0;
    width: 100%;
`;

const Tab = styled.div<{ $active: boolean }>`
    font-size: 14px;
    font-weight: 500;
    color: ${props => props.$active ? '#000000' : '#7B7B7B'};
    border-radius: 8px;
    height: 30px;
    padding: 6px 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    background: ${props => props.$active ? '#E2E5FF' : 'transparent'};
    position: relative;
    bottom: -1px;

    &:hover {
        color: #000000;
    }

    @media (max-width: 768px) {
        font-size: 13px;
        padding: 6px 10px;
        height: 28px;
    }
`;

const InsightsPlaceholder = styled.div`
    width: 100%;
    padding: 60px 20px;
    text-align: center;
    color: #7B7B7B;
    font-size: 16px;
    background: #F6F6F6;
    border-radius: 10px;
    margin-top: 24px;

    @media (max-width: 768px) {
        padding: 40px 16px;
        font-size: 14px;
        margin-top: 16px;
    }
`;